import { z } from "zod";

/** Wire schemas for the publisher `domains` tRPC router. */
export const registrantContactSchema = z
  .object({
    firstName: z.string().trim().min(1).max(128),
    lastName: z.string().trim().min(1).max(128),
    email: z.email().max(256),
    phone: z
      .string()
      .max(32)
      .regex(/^\+\d{1,3}\.\d+$/, "Use E.164 format, for example +1.4155550123"),
    address1: z.string().trim().min(1).max(256),
    address2: z.string().trim().max(256).nullable(),
    city: z.string().trim().min(1).max(128),
    state: z.string().trim().min(1).max(128),
    zip: z.string().trim().min(1).max(32),
    country: z.string().trim().length(2),
  })
  .describe("RegistrantContact");

export const registrantSchema = registrantContactSchema
  .extend({ id: z.string() })
  .describe("Registrant");

export const findRegistrantOutputSchema = z
  .object({ registrant: registrantSchema.nullable() })
  .describe("FindRegistrantOutput");

export const createOrUpdateRegistrantOutputSchema = z
  .object({ registrant: registrantSchema })
  .describe("CreateOrUpdateRegistrantOutput");

export const addressSuggestionSchema = z
  .object({
    placeId: z.string(),
    label: z.string(),
  })
  .describe("AddressSuggestion");

export const autocompleteAddressOutputSchema = z
  .object({ suggestions: z.array(addressSuggestionSchema) })
  .describe("AutocompleteAddressOutput");

/** Fields are null when the resolved place record lacks that component — consumers merge per-field over what the user already typed. */
export const resolvedAddressSchema = z
  .object({
    address1: z.string().nullable(),
    address2: z.string().nullable(),
    city: z.string().nullable(),
    state: z.string().nullable(),
    zip: z.string().nullable(),
    country: z.string().nullable(),
  })
  .describe("ResolvedAddress");

export const resolveAddressOutputSchema = z
  .object({ address: resolvedAddressSchema.nullable() })
  .describe("ResolveAddressOutput");

export const domainSuggestionSchema = z
  .object({
    name: z.string(),
    available: z.boolean(),
    priceCents: z.number().nullable(),
    renewalAmount: z.number().nullable(),
    currency: z.string(),
    premium: z.boolean(),
  })
  .describe("DomainSuggestion");

export const searchDomainsOutputSchema = z
  .object({ suggestions: z.array(domainSuggestionSchema) })
  .describe("SearchDomainsOutput");

export const checkDomainOutputSchema = z
  .object({
    name: z.string(),
    available: z.boolean(),
    priceCents: z.number().nullable(),
    renewalAmount: z.number().nullable(),
    currency: z.string(),
    premium: z.boolean(),
  })
  .describe("CheckDomainOutput");

export const domainRegistrationStatusSchema = z.enum([
  "pending",
  "active",
  "suspended",
  "failed",
  "expired",
]);

/** Statuses that are real at the registrar (suspended is still owned at Cloudflare) — single source of truth for the Prisma and ark/Drizzle consumers. */
export const LIVE_DOMAIN_REGISTRATION_STATUSES = [
  "pending",
  "active",
  "suspended",
] as const satisfies readonly z.infer<typeof domainRegistrationStatusSchema>[];

export const ownedDomainSchema = z
  .object({
    id: z.string(),
    value: z.string(),
    status: domainRegistrationStatusSchema,
    expiresAt: z.string().nullable(),
    priceCents: z.number(),
    renewalAmount: z.number().nullable(),
    autoRenew: z.boolean(),
    currency: z.string(),
    createdAt: z.string(),
  })
  .describe("OwnedDomain");

export const findDomainsOutputSchema = z
  .object({ domains: z.array(ownedDomainSchema) })
  .describe("FindDomainsOutput");

export const purchaseDomainOutputSchema = z
  .object({
    domainRegistrationId: z.string(),
    value: z.string(),
    status: domainRegistrationStatusSchema,
    customDomainId: z.string(),
    liveUrl: z.string(),
  })
  .describe("PurchaseDomainOutput");

export const purchaseEligibilityOutputSchema = z
  .object({
    eligible: z.boolean(),
    reason: z.enum(["upgrade_required"]).nullable(),
  })
  .describe("PurchaseEligibilityOutput");

export const startDomainCheckoutOutputSchema = z
  .object({ confirmationUrl: z.string() })
  .describe("StartDomainCheckoutOutput");

export const domainRegistrationStatusOutputSchema = z
  .object({ status: domainRegistrationStatusSchema.nullable() })
  .describe("DomainRegistrationStatusOutput");

export const paymentMethodPreviewOutputSchema = z
  .object({
    paymentMethod: z
      .object({ brand: z.string(), last4: z.string() })
      .nullable(),
  })
  .describe("PaymentMethodPreviewOutput");

export const setAutoRenewOutputSchema = z
  .object({ autoRenew: z.boolean() })
  .describe("SetAutoRenewOutput");
